import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Icon, Page, SimpleSelect, Skeleton } from 'corso-admin-ui';
import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { CrewMerchantUi } from 'corso-types';
import api from '~/api';
import { AIFeatureCard } from '~/components/ai/AIFeatures';
import MetricCard from '~/components/MetricCard';
import Paginator from '~/components/ui/Paginator';
import { usePaginate } from '~/hooks/usePaginate';
import { useStoreId } from '~/providers/StoreUserProvider';

// --- ReportKeyMetrics --------------------------------------------------------

function ReportKeyMetrics({ report }: { report: CrewMerchantUi.FrontReport }) {
  const { metrics } = report;
  const responseTime = metrics.avgResponseTimeMinutes
    ? Math.round(metrics.avgResponseTimeMinutes)
    : null;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      <MetricCard
        icon="positiveFeedback"
        label="CSAT Score"
        value={metrics.csatScorePercent}
        unit="%"
      />
      <MetricCard
        icon="pending"
        label="Avg Response Time"
        value={responseTime}
        unit=" min"
      />
      <MetricCard
        icon="message"
        label="Messages Sent"
        value={metrics.messagesSent}
      />
      <MetricCard
        icon="positiveFeedback"
        label="Total Csat Scores Collected"
        value={metrics.totalCsatScoresCollected}
      />

      <MetricCard
        icon="add"
        label="New Conversations"
        value={metrics.newConversations}
      />

      <MetricCard
        icon="message"
        label="Messages Received"
        value={metrics.messagesReceived}
      />
    </div>
  );
}

// --- CustomerHighlightsSection -----------------------------------------------

function CustomerHighlightsSection({
  reviews,
}: {
  reviews: CrewMerchantUi.FrontReport['reviews'];
}) {
  const pagination = usePaginate({ items: reviews, perPage: 6 });

  if (reviews.length === 0) return null;

  const reviewKey = (review: CrewMerchantUi.FrontReport['reviews'][number]) =>
    [review.customerName, review.rating, review.comment].join('::');

  return (
    <Card>
      <div className="flex items-start gap-2">
        <Card.Heading>Customer Highlights</Card.Heading>
        <FontAwesomeIcon icon={faStarSolid} className="size-4 text-amber-500" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {pagination.pageItems.map((review) => (
          <Card key={reviewKey(review)} flat>
            <div className="flex items-center gap-2">
              <span className="flex gap-0.5 text-amber-500">
                {Array.from({ length: review.rating }, (_, i) => (
                  <FontAwesomeIcon
                    key={i}
                    icon={faStarSolid}
                    className="h-3 w-3"
                  />
                ))}
              </span>
              <span className="text-xs font-medium text-neutral-500">
                {review.customerName}
              </span>
            </div>
            <p className="text-xs leading-relaxed text-neutral-500 sm:text-sm">
              {review.comment}
            </p>
          </Card>
        ))}
      </div>

      <Paginator pagination={pagination} />
    </Card>
  );
}

function ReportContent({ reports }: { reports: CrewMerchantUi.FrontReport[] }) {
  const [selectedReport, setSelectedReport] = useState(reports[0] ?? null);

  return (
    <>
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex flex-col gap-1 text-xl font-bold text-neutral-900">
            {selectedReport ? `Corso Concierge Report ` : 'Select a Month'}
          </h2>
        </div>
        <div className="sm:min-w-[240px]">
          <SimpleSelect
            value={selectedReport?.id ?? ''}
            onChange={(value) => {
              const report = reports.find((r) => r.id === value) ?? null;
              setSelectedReport(report);
            }}
            label="Select month"
            labelVisuallyHidden
            options={reports.map((report) => ({
              label: `${report.month} ${report.year}`,
              value: report.id,
            }))}
          />
        </div>
      </header>

      <AIFeatureCard
        title="Customer Success Insights"
        description="Every customer interaction is analyzed by AI to identify standout feedback, score sentiment, and continuously improve your support quality. "
      />

      {selectedReport && (
        <>
          <ReportKeyMetrics report={selectedReport} />
          <CustomerHighlightsSection reviews={selectedReport.reviews} />
        </>
      )}
    </>
  );
}

// --- FrontReport (main export) ----------------------------------------------

export const useFrontReports = () => {
  const storeId = useStoreId();
  return useQuery({
    queryKey: ['front-reports', storeId],
    queryFn: () => api.store(storeId).frontReport(),
  });
};

function ReportContentLoading() {
  return (
    <div className="space-y-4">
      <Card>
        <div className="mb-4">
          <Skeleton.Rectangle height="2rem" width="12rem" />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }, (_, i) => (
            <Card flat key={`metric-skeleton-${i}`}>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Skeleton.Rectangle height="0.75rem" width="5rem" />
                  <Skeleton.Circle diameter="2rem" />
                </div>
                <Skeleton.Rectangle height="2rem" width="4rem" />
              </div>
            </Card>
          ))}
        </div>
      </Card>
      <Card>
        <div className="mb-4">
          <Skeleton.Rectangle height="1.5rem" width="10rem" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }, (_, i) => (
            <Card flat key={`highlight-skeleton-${i}`}>
              <Skeleton.TextBlock lines={3} />
            </Card>
          ))}
        </div>
      </Card>
    </div>
  );
}

function ReportContentEmpty() {
  return (
    <Card>
      <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
        <div className="flex items-center justify-center rounded-full bg-neutral-100 p-4">
          <Icon icon="analytics" className="h-10 w-10 text-neutral-400" />
        </div>
        <div className="space-y-1">
          <p className="text-lg font-semibold text-neutral-900">
            No Reports Available
          </p>
          <p className="text-sm text-neutral-500">
            Customer success reports will appear here once data is available.
          </p>
        </div>
      </div>
    </Card>
  );
}

export default function CXReportPage() {
  const { data, isLoading } = useFrontReports();
  const reports = data ?? [];

  if (isLoading)
    return (
      <Page title="">
        <ReportContentLoading />
      </Page>
    );

  return (
    // todo: explore empty title / actions
    <Page title="">
      <div className="space-y-4">
        {reports.length > 0 ? (
          <ReportContent reports={reports} />
        ) : (
          <ReportContentEmpty />
        )}
      </div>
    </Page>
  );
}
